import { ArrowBackIcon } from "@chakra-ui/icons";
import {
  HStack,
  VStack,
  Link,
  Button,
  Text,
  Spinner,
  Box,
  Icon,
  BoxProps,
  Grid,
  useToast,
} from "@chakra-ui/react";
import {
  KYDFilters,
  KYDNumericOperator,
  KYDSegment,
} from "common/interfaces/KYDCampaign";
import { useState, useEffect, useCallback } from "react";
import actions from "src/actions";
import { useParams } from "react-router-dom";
import { FiMail, FiShoppingCart, FiUser } from "react-icons/fi";
import StatCard from "../../Components/StatCard";
import { genderToText } from "common/services/util/campaigns";
import { IoPricetagsOutline } from "react-icons/io5";
import CreateNewSegmentDialog from "../CreateNewSegmentDialog";
import { Link as RouterLink } from "react-router-dom";
import { KYDScope } from "@common/interfaces/ICognito";
import ShowIfHasScope from "src/Manager/ShowIfHasScope";
import { KYDGenreLabel } from "src/Manager/Genres";
import { dineroFormatCurrency } from "src/utils";
import {
  EventFilterSummary,
  PerformerFilterSummary,
  VenueFilterSummary,
} from "../CreateNewSegmentDialog/CreateNewSegmentReview";
import SegmentDailyMetricsChart from "./SegmentDailyMetricsChart";

const Card = ({
  title,
  children,
  icon,
  ...props
}: {
  title: string;
  children: React.ReactNode;
  icon: React.ReactNode;
} & BoxProps) => {
  return (
    <VStack
      alignSelf="stretch"
      alignItems="flex-start"
      w="100%"
      h="100%"
      borderRadius="10px"
      border="1px solid #E6E6E6"
      background="#FFFFFF"
      boxShadow="0px 10px 15px -5px rgba(247, 247, 247, 0.90)"
      p="16px"
      {...props}
    >
      <HStack
        alignItems="center"
        justifyContent="space-between"
        w="100%"
        mb={2}
      >
        <Text
          color="rgba(34, 34, 34, 0.80)"
          fontSize="16px"
          fontWeight={500}
          lineHeight="23px"
          letterSpacing="-0.48px"
        >
          {title}
        </Text>
        {icon}
      </HStack>
      {children}
    </VStack>
  );
};

const FilterSummary = ({
  title,
  count,
  values,
}: {
  title: string;
  count: string;
  values: string[];
}) => {
  return (
    <VStack gap={2.5}>
      <HStack w="100%">
        <Text color="#718096" fontSize="14px" fontWeight={500}>
          {title}
        </Text>
        <Box
          bg="#666"
          borderRadius="20px"
          px={1.5}
          py={0.75}
          color="white"
          fontSize="12px"
        >
          {count}
        </Box>
      </HStack>
      <HStack gap={2.5} w="100%" flexWrap="wrap">
        {values.map((value) => (
          <Box
            key={value}
            bg="#EDF2F7"
            borderRadius="4px"
            px={1.5}
            color="rgba(0, 0, 0, 0.70)"
            fontWeight={500}
            fontSize="14px"
            lineHeight="23px"
          >
            {value}
          </Box>
        ))}
      </HStack>
    </VStack>
  );
};

const NumericValue = ({
  title,
  value,
  operator,
}: {
  title: string;
  value: string;
  operator?: KYDNumericOperator;
}) => {
  return (
    <VStack align="flex-start">
      <Text color="#718096" fontSize="14px" fontWeight={500}>
        {title}
      </Text>
      <Text color="#1A202C" fontSize="20px">
        {operator} {value}
      </Text>
    </VStack>
  );
};

export type KYDSegmentWithVenues = KYDSegment;

const SegmentDetail = ({ permissions = [] }: { permissions?: KYDScope[] }) => {
  const [segment, setSegment] = useState<KYDSegmentWithVenues | null>(null);
  const { id } = useParams();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const fetchSegment = useCallback(async () => {
    try {
      const segment = await actions.ADMIN.fetchSegment(id);
      setSegment(segment);
    } catch (err) {
      console.log(err);
    }
  }, [id]);

  useEffect(() => {
    fetchSegment();
  }, [fetchSegment]);

  const handleExportCSV = useCallback(async () => {
    try {
      setLoading(true);
      const { url } = await actions.ADMIN.exportSegment(id);
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to download CSV");
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${segment?.name || `segment-${id}`}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(objectUrl);

      toast({
        title: "Export started",
        description: "Your CSV download should begin shortly.",
        status: "success",
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Export failed",
        description: error.message || "Unable to export segment CSV.",
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [id, segment?.name, toast]);

  const hasPurchaseBehaviourFilters =
    segment?.filters?.average_order_value.value > 0 ||
    segment?.filters.average_items_per_order.value > 0 ||
    segment?.filters.order_count.value > 0 ||
    segment?.filters.total_attendance.value > 0;

  const hasDemographicsFilters =
    segment?.filters?.age.value > 0 ||
    (segment?.filters?.gender !== null && segment?.filters?.gender !== "any");

  const hasEventBasedFilters =
    segment?.filters?.genres?.length > 0 ||
    segment?.filters?.venues?.length > 0 ||
    segment?.filters?.performers?.length > 0 ||
    segment?.filters?.events?.length > 0;

  const hasAnyFilters =
    hasEventBasedFilters ||
    hasDemographicsFilters ||
    hasPurchaseBehaviourFilters ||
    segment?.filters?.custom_tags?.length > 0;

  if (!segment) return <Spinner />;
  return (
    <VStack py={4}>
      {isEditDialogOpen && (
        <CreateNewSegmentDialog
          isOpen={isEditDialogOpen}
          close={() => setIsEditDialogOpen(false)}
          segment={segment}
          refetch={() => {
            fetchSegment();
          }}
        />
      )}
      <HStack w="100%" h="100%" justifyContent="space-between">
        <VStack alignItems="flex-start">
          <Link as={RouterLink} to="/campaigns/segments">
            <Button
              variant="link"
              color="#888888"
              fontWeight="500"
              fontSize="14px"
              leftIcon={<ArrowBackIcon />}
            >
              All Segments
            </Button>
          </Link>
          <Text
            fontSize="24px"
            color="#1A202C"
            fontWeight="500"
            overflow="hidden"
            textOverflow="ellipsis"
            lineHeight="30px"
            letterSpacing="-0.72px"
          >
            {segment?.name}
          </Text>
        </VStack>
        <HStack gap={"16px"}>
          <ShowIfHasScope
            permissions={permissions}
            checkFor={["crm/segments:read", "crm/read"]}
          >
            <Button
              onClick={handleExportCSV}
              bgColor={"#FFFFFF"}
              color={"#000000"}
              borderRadius={"100px"}
              fontWeight={"600"}
              fontSize={"13px"}
              isLoading={loading}
              isDisabled={loading}
              padding={"14px 16px 14px 16px"}
              border={"1px solid rgba(0, 0, 0, 0.10)"}
            >
              Export To CSV
            </Button>
          </ShowIfHasScope>
          <ShowIfHasScope
            permissions={permissions}
            checkFor={["crm/segments:write", "crm/write"]}
          >
            <Button
              onClick={() => {
                setIsEditDialogOpen(true);
              }}
              bgColor={"#000000"}
              color={"#FFFFFF"}
              borderRadius={"1000px"}
              fontWeight={"600"}
              fontSize={"13px"}
              padding={"14px 16px 14px 16px"}
            >
              Edit Segment
            </Button>
          </ShowIfHasScope>
        </HStack>
      </HStack>

      <SegmentDailyMetricsChart
        dailyMetrics={segment.daily_metrics || []}
        monthlyMetrics={segment.monthly_metrics || []}
        segment={segment}
      />

      <VStack
        mt="24px"
        alignItems={"flex-start"}
        w={"100%"}
        borderRadius={"8px"}
        border={"1px solid rgba(0, 0, 0, 0.05)"}
        px={"24px"}
        py={"12px"}
        background={
          "linear-gradient(180deg, rgba(231, 233, 239, 0.01) 0%, rgba(255, 255, 255, 0.75) 100%), linear-gradient(0deg, #F7F7F7 0%, #F7F7F7 100%), #FFF"
        }
      >
        <Text fontSize="24px" fontWeight="600" mb={"12px"}>
          Filter Summary
        </Text>

        {hasAnyFilters ? (
          <Grid
            templateColumns="repeat(auto-fit, minmax(280px, auto))"
            width={"100%"}
            gap="16px"
            alignItems="stretch"
            flex="1"
          >
            {hasEventBasedFilters && (
              <Card
                title="Event Based Filter"
                icon={<Icon as={FiMail} fontSize="24px" color="#969696" />}
              >
                <HStack w={"100%"} gap={"16px"} justifyContent="space-between">
                  {segment?.filters?.genres.length > 0 && (
                    <FilterSummary
                      title="Genre"
                      count={segment.filters.genres.length.toString()}
                      values={segment.filters.genres.map(
                        (genre) => KYDGenreLabel[genre] || genre
                      )}
                    />
                  )}
                  {segment.filters.events.length > 0 && (
                    <EventFilterSummary
                      events={
                        segment.filters?.events_display?.map(
                          (event) => event.label || event.value
                        ) ||
                        segment.filters?.events?.map((event) => event) ||
                        []
                      }
                    />
                  )}
                  {segment.filters.performers.length > 0 && (
                    <PerformerFilterSummary
                      performers={
                        segment.filters?.performers_display?.map(
                          (performer) => performer.label || performer.value
                        ) ||
                        segment.filters?.performers?.map(
                          (performer) => performer
                        ) ||
                        []
                      }
                    />
                  )}
                  {segment?.filters?.venues.length > 0 && (
                    <VenueFilterSummary
                      venues={
                        segment.filters?.venues_display?.map(
                          (venue) => venue.label || venue.value
                        ) ||
                        segment.filters?.venues?.map((venue) => venue) ||
                        []
                      }
                    />
                  )}
                </HStack>
              </Card>
            )}
            {hasDemographicsFilters && (
              <Card
                title="Demographics"
                icon={<Icon as={FiUser} fontSize="24px" color="#969696" />}
                height={"100%"}
              >
                <HStack gap={12} w="100%" justifyContent="flex-start">
                  {segment.filters.age.value > 0 && (
                    <NumericValue
                      title="Age"
                      value={segment.filters.age.value.toString()}
                      operator={segment.filters.age.operator}
                    />
                  )}
                  {segment.filters.gender !== null &&
                    segment.filters.gender !== "any" && (
                      <NumericValue
                        title="Gender"
                        value={genderToText(segment.filters.gender)}
                      />
                    )}
                </HStack>
              </Card>
            )}
            {hasPurchaseBehaviourFilters && (
              <Card
                title="Purchase Behaviour"
                icon={
                  <Icon as={FiShoppingCart} fontSize="24px" color="#969696" />
                }
              >
                <HStack gap={6} w="100%" justifyContent="space-between">
                  {segment.filters.average_order_value.value > 0 && (
                    <NumericValue
                      title="Avg. Basket Size"
                      value={dineroFormatCurrency(
                        segment.filters.average_order_value.value * 100
                      )}
                      operator={segment.filters.average_order_value.operator}
                    />
                  )}
                  {segment.filters.average_items_per_order.value > 0 && (
                    <NumericValue
                      title="Items"
                      value={segment.filters.average_items_per_order.value.toString()}
                      operator={
                        segment.filters.average_items_per_order.operator
                      }
                    />
                  )}
                  {segment.filters.order_count.value > 0 && (
                    <NumericValue
                      title="Frequency"
                      value={segment.filters.order_count.value.toString()}
                      operator={segment.filters.order_count.operator}
                    />
                  )}
                  {segment.filters.total_attendance.value > 0 && (
                    <NumericValue
                      title="Attended"
                      value={segment.filters.total_attendance.value.toString()}
                      operator={segment.filters.total_attendance.operator}
                    />
                  )}
                </HStack>
              </Card>
            )}
            {segment.filters.custom_tags.length > 0 && (
              <Card
                hidden={segment.filters.custom_tags.length === 0}
                flex={1}
                title="Tags"
                icon={
                  <Icon
                    as={IoPricetagsOutline}
                    fontSize="24px"
                    color="#969696"
                  />
                }
                h="100%"
              >
                {segment.filters.custom_tags.length > 0 ? (
                  <HStack
                    gap={6}
                    w="100%"
                    align="flex-start"
                    justifyContent="space-between"
                    h="100%"
                    flexWrap="wrap"
                  >
                    {segment.filters.custom_tags.map((tag) => (
                      <Box
                        key={tag}
                        bg="#EDF2F7"
                        borderRadius="4px"
                        px={1.5}
                        color="rgba(0, 0, 0, 0.70)"
                        fontWeight={500}
                        fontSize="14px"
                        lineHeight="23px"
                      >
                        {tag}
                      </Box>
                    ))}
                  </HStack>
                ) : null}
              </Card>
            )}
          </Grid>
        ) : (
          <Text fontSize="16px" color="#718096">
            This segment has no filters applied.
          </Text>
        )}
      </VStack>
    </VStack>
  );
};

export default SegmentDetail;
